import { ReactNode, useCallback } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useTheme } from "@/hooks/use-theme";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Dumbbell, Calendar, Users, Activity, Settings, 
  CreditCard, LayoutDashboard, LogOut, Package, 
  Menu, X, Bell, Search, Compass, DollarSign,
  Palette, Shield, ShieldAlert, User, BarChart3, Building2, AlertTriangle, Crown,
  MessageSquare, UserPlus, Percent, Send, LifeBuoy,
  Sun, Moon, Sparkles, Bot, Megaphone, UserCog, XCircle, FileText, Inbox, KeyRound
} from "lucide-react";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/notifications";
import { FeedbackDialog } from "@/components/feedback-dialog";

function ImpersonationBanner() {
  const { user, refreshUser } = useAuth();
  const [, setLocation] = useLocation();
  const isImpersonating = localStorage.getItem("impersonating") === "true";

  const exit = useCallback(async () => {
    const original = localStorage.getItem("originalToken");
    if (original) localStorage.setItem("token", original);
    localStorage.removeItem("impersonating");
    localStorage.removeItem("originalToken");
    await refreshUser();
    setLocation("/admin/dashboard");
  }, [refreshUser, setLocation]);

  if (!isImpersonating) return null;

  return (
    <div className="sticky top-0 z-50 w-full bg-amber-500/90 backdrop-blur-sm border-b border-amber-400/50 px-4 py-2 flex items-center justify-between gap-4">
      <div className="flex items-center gap-2 text-amber-950 text-sm font-semibold">
        <UserCog className="w-4 h-4 shrink-0" />
        <span>
          Impersonating <strong>{user?.firstName} {user?.lastName}</strong>
          <span className="font-normal ml-1 text-amber-900">({user?.role?.toLowerCase()})</span>
        </span>
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={exit}
        className="text-amber-950 hover:bg-amber-400/40 hover:text-amber-950 gap-1.5 font-semibold shrink-0"
      >
        <XCircle className="w-4 h-4" />
        Exit Impersonation
      </Button>
    </div>
  );
}

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggleTheme}
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      className="text-muted-foreground hover:text-foreground"
    >
      {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
    </Button>
  );
}

interface LayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const { data: notifData } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => apiFetch<{ notifications: any[]; unreadCount: number }>("/notifications/my"),
    refetchInterval: 30000,
    retry: false,
    enabled: !!user,
  });
  const unreadCount = notifData?.unreadCount ?? 0;

  if (!user) return <>{children}</>;

  const navItems = {
    STUDENT: [
      { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
      { name: "Book Session", href: "/book", icon: Dumbbell },
      { name: "Find a Trainer", href: "/find-trainer", icon: Sparkles },
      { name: "Gym Schedule", href: "/schedule", icon: Activity },
      { name: "Gym Calendar", href: "/master-calendar", icon: Calendar },
      { name: "My Gym", href: "/explore/gyms", icon: Compass },
      { name: "Find Trainers", href: "/explore/trainers", icon: Search },

      { name: "My Sessions", href: "/sessions", icon: Calendar },
      { name: "Packages", href: "/packages", icon: Package },
      { name: "Membership", href: "/memberships", icon: CreditCard },
      { name: "Messages", href: "/messages", icon: MessageSquare },
      { name: "Profile", href: "/profile", icon: User },
      { name: "Notifications", href: "/notifications", icon: Inbox },
      { name: "Tech Support", href: "mailto:support@openmat.com", icon: LifeBuoy, external: true },
    ],
    TRAINER: [
      { name: "Dashboard", href: "/trainer/dashboard", icon: LayoutDashboard },
      { name: "Sessions", href: "/trainer/sessions", icon: Calendar },
      { name: "My Clients", href: "/trainer/clients", icon: Users },
      { name: "Availability", href: "/trainer/availability", icon: Activity },
      { name: "Payouts", href: "/trainer/payouts", icon: DollarSign },
      { name: "Performance", href: "/trainer/performance", icon: BarChart3 },
      { name: "Messages", href: "/trainer/messages", icon: MessageSquare },
      { name: "Profile", href: "/trainer/profile", icon: User },
      { name: "Notifications", href: "/notifications", icon: Inbox },
      { name: "Tech Support", href: "mailto:support@openmat.com", icon: LifeBuoy, external: true },
    ],
    ADMIN: [
      { name: "Dashboard", href: "/admin/dashboard", icon: LayoutDashboard },
      { name: "Master Calendar", href: "/admin/calendar", icon: Calendar },
      { name: "Revenue Split", href: "/admin/revenue-split", icon: Percent },
      { name: "Trainers", href: "/admin/trainers", icon: Users },
      { name: "Students", href: "/admin/students", icon: Users },
      { name: "Payroll", href: "/admin/payroll", icon: DollarSign },
      { name: "Analytics", href: "/admin/analytics", icon: BarChart3 },
      { name: "Engagement", href: "/admin/engagement", icon: Activity },
      { name: "Session Notes", href: "/admin/session-notes", icon: FileText },
      { name: "Memberships", href: "/admin/memberships", icon: Crown },
      { name: "Billing", href: "/admin/billing", icon: CreditCard },
      { name: "Branding", href: "/admin/branding", icon: Palette },
      { name: "AI Assistant", href: "/admin/ai-assistant", icon: Bot },
      { name: "Cancellation", href: "/admin/cancellation", icon: Shield },
      { name: "No-Shows", href: "/admin/no-shows", icon: AlertTriangle },
      { name: "Messaging", href: "/admin/messaging", icon: Send },
      { name: "Broadcast Messages", href: "/admin/broadcast-messages", icon: Megaphone },
      { name: "Sessions", href: "/admin/sessions", icon: KeyRound },
      { name: "Login Security", href: "/admin/security", icon: ShieldAlert },
      { name: "Settings", href: "/admin/settings", icon: Settings },
      { name: "Tech Support", href: "mailto:support@openmat.com", icon: LifeBuoy, external: true },
    ],
    SUPERADMIN: [
      { name: "Dashboard", href: "/superadmin", icon: LayoutDashboard },
      { name: "Gyms", href: "/superadmin/gyms", icon: Building2 },
      { name: "Users", href: "/superadmin/users", icon: Users },
      { name: "Analytics", href: "/superadmin/analytics", icon: BarChart3 },
      { name: "Tech Support", href: "mailto:support@openmat.com", icon: LifeBuoy, external: true },
    ]
  };

  const links = navItems[user.role as keyof typeof navItems] || [];

  const homeHref = user.role === "ADMIN" ? "/admin/dashboard"
    : user.role === "TRAINER" ? "/trainer/dashboard"
    : user.role === "SUPERADMIN" ? "/superadmin"
    : "/dashboard";

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col md:flex-row font-sans">
      <ImpersonationBanner />
      {/* Mobile Header */}
      <header className="md:hidden flex items-center justify-between p-4 border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-50">
        <Link href={homeHref} className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary" aria-hidden="true" />
          <span className="font-display font-bold text-2xl uppercase tracking-widest text-foreground">OPEN MAT</span>
        </Link>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <NotificationBell />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Toggle navigation menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Sidebar */}
      <AnimatePresence>
        {(mobileMenuOpen || window.innerWidth >= 768) && (
          <motion.aside
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: "spring", bounce: 0, duration: 0.4 }}
            className={cn(
              "fixed md:sticky top-0 left-0 h-screen w-64 glass-panel border-r border-border z-40 flex flex-col",
              "md:translate-x-0 transition-transform duration-300",
              mobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
            )}
          >
            <Link href={homeHref} className="p-6 hidden md:flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-primary" aria-hidden="true" />
              <span className="font-display font-bold text-3xl uppercase tracking-widest text-primary">OPEN MAT</span>
            </Link>

            <div className="px-6 py-4 border-b border-border flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                {user.firstName[0]}{user.lastName[0]}
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-bold text-foreground">{user.firstName} {user.lastName}</span>
                <span className="text-xs text-muted-foreground capitalize">{user.role.toLowerCase()}</span>
              </div>
            </div>

            <nav className="flex-1 py-6 px-4 space-y-2 overflow-y-auto">
              {links.map((link) => {
                const linkPath = link.href.split("?")[0];
                const isActive = !link.external && (location === link.href || location === linkPath || location.startsWith(`${linkPath}/`));
                const Icon = link.icon;
                const isNotifications = link.name === "Notifications";
                const itemContent = (
                  <div className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 cursor-pointer group",
                    isActive 
                      ? "bg-primary text-primary-foreground font-semibold shadow-lg shadow-primary/20" 
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}>
                    <Icon className={cn("w-5 h-5 transition-transform group-hover:scale-110", isActive ? "text-primary-foreground" : "text-muted-foreground group-hover:text-primary")} />
                    <span className="flex-1">{link.name}</span>
                    {isNotifications && unreadCount > 0 && (
                      <span className={cn(
                        "min-w-[20px] h-5 px-1 rounded-full text-[11px] font-bold flex items-center justify-center leading-none",
                        isActive ? "bg-black/20 text-white" : "bg-red-500 text-white"
                      )}>
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </div>
                );
                if (link.external) {
                  return (
                    <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
                      {itemContent}
                    </a>
                  );
                }
                return (
                  <Link key={link.href} href={link.href} onClick={() => setMobileMenuOpen(false)}>
                    {itemContent}
                  </Link>
                );
              })}
            </nav>

            <div className="p-4 border-t border-border">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                onClick={logout}
              >
                <LogOut className="w-5 h-5 mr-3" />
                Sign Out
              </Button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      <FeedbackDialog />

      {/* Main Content */}
      <main className="flex-1 relative overflow-x-hidden bg-background">
        {/* Desktop Topbar */}
        <div className="hidden md:flex h-20 items-center justify-end gap-2 px-8 border-b border-border glass-panel sticky top-0 z-30">
          <ThemeToggle />
          <NotificationBell />
        </div>
        
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={location}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

export function SmartLayout({ children }: LayoutProps) {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) {
    return <AppLayout>{children}</AppLayout>;
  }
  return <PublicLayout>{children}</PublicLayout>;
}

function scrollToSection(id: string, retries = 60): void {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth" });
  } else if (retries > 0) {
    requestAnimationFrame(() => scrollToSection(id, retries - 1));
  }
}

export function PublicLayout({ children }: LayoutProps) {
  const [location, setLocation] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleAnchorClick = (sectionId: string) => (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (location === "/") {
      scrollToSection(sectionId);
    } else {
      setLocation("/");
      scrollToSection(sectionId);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-sans flex flex-col">
      <header className="h-16 border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-50 px-6 lg:px-12 flex items-center justify-between">
        <Link href="/">
          <div className="flex items-center gap-2.5 cursor-pointer group">
            <div className="w-8 h-8 bg-primary group-hover:scale-105 transition-transform" aria-hidden="true" />
            <div className="flex flex-col">
              <span className="font-display font-bold text-2xl uppercase tracking-[0.15em] text-foreground">OPEN MAT</span>
              <span className="text-[10px] text-muted-foreground tracking-wide leading-tight">Your personal training program manager</span>
            </div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          <a href="/#trainers" onClick={handleAnchorClick("trainers")}><span className="text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer tracking-wide uppercase" style={{fontFamily:"'DM Mono', monospace", letterSpacing:"0.1em"}}>For Trainers</span></a>
          <a href="/#students" onClick={handleAnchorClick("students")}><span className="text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer tracking-wide uppercase" style={{fontFamily:"'DM Mono', monospace", letterSpacing:"0.1em"}}>For Students</span></a>
          <Link href="/pricing"><span className="text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer tracking-wide uppercase" style={{fontFamily:"'DM Mono', monospace", letterSpacing:"0.1em"}}>Gym Pricing</span></Link>
          <Link href="/login"><span className="text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer tracking-wide uppercase" style={{fontFamily:"'DM Mono', monospace", letterSpacing:"0.1em"}}>Sign In/Up</span></Link>
        </nav>
        <div className="flex items-center gap-3">
          <Link href="/register-gym">
            <Button size="sm" className="hidden sm:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-sm px-5 text-[13px] hover:-translate-y-0.5 transition-all shadow-lg shadow-primary/20 uppercase tracking-widest" style={{fontFamily:"'DM Mono', monospace"}}>
              Sign Up Your Gym
            </Button>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-foreground"
            aria-label="Toggle navigation menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden border-b border-border bg-background/95 backdrop-blur-xl sticky top-16 z-40 overflow-hidden"
          >
            <nav className="flex flex-col px-6 py-4 gap-4">
              <a href="/#trainers" onClick={handleAnchorClick("trainers")}><span className="text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer tracking-wide uppercase" style={{fontFamily:"'DM Mono', monospace", letterSpacing:"0.1em"}}>For Trainers</span></a>
              <a href="/#students" onClick={handleAnchorClick("students")}><span className="text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer tracking-wide uppercase" style={{fontFamily:"'DM Mono', monospace", letterSpacing:"0.1em"}}>For Students</span></a>
              <Link href="/pricing" onClick={() => setMobileMenuOpen(false)}><span className="text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer tracking-wide uppercase" style={{fontFamily:"'DM Mono', monospace", letterSpacing:"0.1em"}}>Pricing</span></Link>
              <Link href="/login" onClick={() => setMobileMenuOpen(false)}><span className="text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer tracking-wide uppercase" style={{fontFamily:"'DM Mono', monospace", letterSpacing:"0.1em"}}>Sign In/Up</span></Link>
              <Link href="/register-gym" onClick={() => setMobileMenuOpen(false)}>
                <Button size="sm" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold rounded-sm px-5 text-[13px] uppercase tracking-widest" style={{fontFamily:"'DM Mono', monospace"}}>
                  Sign Up Your Gym
                </Button>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
      <main className="flex-1 flex flex-col">
        {children}
      </main>
      <footer className="py-16 border-t border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-6 h-6 bg-primary opacity-40" aria-hidden="true" />
                <span className="font-display font-bold text-base uppercase tracking-[0.15em] text-muted-foreground/50">OPEN MAT</span>
              </div>
              <p className="text-[13px] text-muted-foreground/50 leading-relaxed">
                Private lesson management software for martial arts gyms. Automate booking, payments, and payouts.
              </p>
            </div>
            <nav aria-label="Platform links">
              <h3 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wider mb-4">Platform</h3>
              <ul className="space-y-2.5 text-[13px] text-muted-foreground/60">
                <li><Link href="/explore/gyms"><span className="hover:text-foreground/60 transition-colors cursor-pointer">My Gym</span></Link></li>
                <li><Link href="/explore/trainers"><span className="hover:text-foreground/60 transition-colors cursor-pointer">Find Trainers</span></Link></li>
                <li><Link href="/pricing"><span className="hover:text-foreground/60 transition-colors cursor-pointer">Pricing</span></Link></li>
                <li><Link href="/pricing#faq"><span className="hover:text-foreground/60 transition-colors cursor-pointer">FAQ</span></Link></li>
              </ul>
            </nav>
            <nav aria-label="Gym owner links">
              <h3 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wider mb-4">For Gym Owners</h3>
              <ul className="space-y-2.5 text-[13px] text-muted-foreground/60">
                <li><Link href="/register-gym"><span className="hover:text-foreground/60 transition-colors cursor-pointer">Sign Up Your Gym</span></Link></li>
                <li><Link href="/pricing"><span className="hover:text-foreground/60 transition-colors cursor-pointer">Compare Plans</span></Link></li>
                <li><Link href="/login"><span className="hover:text-foreground/60 transition-colors cursor-pointer">Gym Dashboard</span></Link></li>
              </ul>
            </nav>
            <nav aria-label="Account links">
              <h3 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wider mb-4">Account</h3>
              <ul className="space-y-2.5 text-[13px] text-muted-foreground/60">
                <li><Link href="/login"><span className="hover:text-foreground/60 transition-colors cursor-pointer">Log In</span></Link></li>
                <li><Link href="/register"><span className="hover:text-foreground/60 transition-colors cursor-pointer">Sign Up</span></Link></li>
                <li><a href="mailto:support@openmat.com" target="_blank" rel="noopener noreferrer"><span className="hover:text-foreground/60 transition-colors cursor-pointer">Tech Support</span></a></li>
              </ul>
            </nav>
          </div>
          <div className="border-t border-border pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[13px] text-muted-foreground/40">&copy; {new Date().getFullYear()} OPEN MAT. All rights reserved.</p>
            <div className="flex items-center gap-6 text-[13px] text-muted-foreground/40">
              <Link href="/pricing"><span className="hover:text-muted-foreground/60 transition-colors cursor-pointer">Pricing</span></Link>
              <Link href="/explore/gyms"><span className="hover:text-muted-foreground/60 transition-colors cursor-pointer">Gyms</span></Link>
              <Link href="/explore/trainers"><span className="hover:text-muted-foreground/60 transition-colors cursor-pointer">Trainers</span></Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
